package com.shadowings.gero.data.lessons.chapter13

import com.shadowings.gero.data.lessons.inlineMeaning
import androidx.compose.ui.unit.dp
import com.shadowings.gero.data.lessons.buildVocabQuizContent
import com.shadowings.gero.data.lessons.chapter12.buildMultiPress
import com.shadowings.gero.data.lessons.toHieroExplanation
import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_parole_del_tempo_1
import gero.composeapp.generated.resources.exp_content_word_reading_and_meaning
import gero.composeapp.generated.resources.exp_title_parole_del_tempo
import gero.composeapp.generated.resources.exp_title_word_name

// Vocabulary needed for the ḏr ("since") lesson that follows: time-expression
// words (pꜣwt/rk/sf/dwꜣt) plus the N9 (moon, phonogram psḏ) sign new to this
// lesson, none of which had a home in the app's existing vocabulary.
val paroleDelTempoWordIds = listOf("ALBA_DEI_TEMPI", "EPOCA", "IERI", "STAMATTINA")

fun getParoleDelTempoContent(
    hiero: List<Hiero>,
    words: List<Word>,
    languageCode: String
): List<IExplanation> {
    val intro = listOf(
        ExplanationContent(
            title = Res.string.exp_title_parole_del_tempo,
            content = Res.string.exp_content_parole_del_tempo_1,
            face = Faces.Happy,
            character = Characters.Alyn,
        )
    )

    val hieroSlide = listOf("N9").toHieroExplanation(hiero, languageCode)

    val wordSlides = paroleDelTempoWordIds
        .mapNotNull { id -> words.firstOrNull { it.id == id } }
        .map { word ->
            ExplanationContent(
                title = Res.string.exp_title_word_name,
                titleArgs = listOf(word.getMeaning(languageCode)),
                content = Res.string.exp_content_word_reading_and_meaning,
                contentArgs = listOf(
                    word.reading,
                    word.inlineMeaning(languageCode),
                    word.getNote(languageCode)
                ),
                face = Faces.Happy,
                character = Characters.Nilo,
                composable = { MdCWord(word.gardiner, rtl = false, cart = false, size = 96.dp) },
                wordId = word.id
            )
        }

    return intro + hieroSlide + wordSlides
}

// A handful of already-taught nouns, used only as extra multiple-choice
// distractors/review filler (buildVocabQuizContent's additionalOptions, and
// the "mixed" buildMultiPress round) - not re-taught here.
private val paroleDelTempoOldWordIds = listOf("CASA", "TEMPIO", "ALTARE", "TETTO", "HORUS", "OSIRIDE")

fun getParoleDelTempoQuizContent(
    hiero: List<Hiero>,
    words: List<Word>,
    languageCode: String
): List<IQuizContent> {
    val explanations = getParoleDelTempoContent(hiero, words, languageCode)

    val baseQuizzes = buildVocabQuizContent(
        explanations = explanations,
        words = words,
        languageCode = languageCode,
        additionalOptions = paroleDelTempoOldWordIds.toSet()
    )

    val newWords = paroleDelTempoWordIds.mapNotNull { id -> words.firstOrNull { it.id == id } }
    val oldWords = paroleDelTempoOldWordIds.mapNotNull { id -> words.firstOrNull { it.id == id } }

    val multiPressNew = buildMultiPress(newWords.shuffled(), languageCode)
    val multiPressMixed = buildMultiPress(
        (newWords.shuffled().take(2) + oldWords.shuffled().take(2)).shuffled(),
        languageCode
    )

    return (baseQuizzes + multiPressNew + multiPressMixed).shuffled()
}
